/**
 * Showcase embed: vriti.ai's `/products/ai-interview` page frames our marketing landing
 * (`/?vriti_showcase=1`) under its own header. That frame shows the landing and nothing else:
 *
 *  - the landing renders as it does for a visitor (`useAuthStatus` reports signed-out), so it never
 *    offers "Open dashboard";
 *  - every link to another page (sign-up, log-in, overview, blog, anything) opens on
 *    interviews.vriti.ai in a new tab. Only in-page `#section` jumps stay in the frame.
 *
 * Opt-in by the query param only, and only while actually framed: the standalone app, the ATS embed
 * (`?ats_connector`) and Vriti's child-service embed (`accessed_from`) never set it, so they are
 * untouched.
 */
const PARAM = 'vriti_showcase';
let installed = false;
/** True in the showcase frame once installed — the landing then renders as for a visitor. */
export const isShowcaseEmbedActive = () => installed;
const isShowcaseEmbed = () => {
    try {
        return (window.self !== window.top && new URLSearchParams(window.location.search).get(PARAM) === '1');
    }
    catch (_a) {
        return false;
    }
};
/** Installs the click handler once per page load, and only in the showcase frame. */
export const installShowcaseEmbed = () => {
    if (installed || typeof window === 'undefined' || !isShowcaseEmbed())
        return;
    installed = true;
    // Capture phase on the document runs before React Router's own click handling, so the link
    // never navigates the frame.
    document.addEventListener('click', (e) => {
        var _a, _b;
        if (e.defaultPrevented ||
            e.button !== 0 ||
            e.metaKey ||
            e.ctrlKey ||
            e.shiftKey ||
            e.altKey) {
            return;
        }
        const a = (_b = (_a = e.target) === null || _a === void 0 ? void 0 : _a.closest) === null || _b === void 0 ? void 0 : _b.call(_a, 'a[href]');
        if (!a || (a.target && a.target !== '_self'))
            return;
        const url = new URL(a.href, window.location.href);
        // External links already open in a new tab; a `#section` jump on this page stays here.
        if (url.origin !== window.location.origin)
            return;
        if (url.pathname === window.location.pathname && url.hash)
            return;
        e.preventDefault();
        e.stopPropagation();
        window.open(url.href, '_blank', 'noopener,noreferrer');
    }, true);
};
